import { ScheduleSourceType } from "@common/types/domains/accounting/schedule";
import { CellContext } from "@tanstack/react-table";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { memo, useCallback, useMemo } from "react";
import { InvoiceLineItem } from "@common/types/invoice";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import { useViewTableMeta } from "@hooks/view/useViewTableMeta";
import { useTypedFlags } from "@hooks/useTypedFlags";
import {
    opensScheduleModalBeforeApply,
    resolveInvoiceTemplateBlockReason,
    SchedulePickerTemplateWithAnchors,
} from "@components/accounting/schedules/scheduleApplyConfirmHelpers";
import { SchedulePicker } from "@components/accounting/schedules/SchedulePicker";
import { getInvoiceScheduleAmount } from "@components/accounting/schedules/scheduleApplyInvoiceHelpers";
import { filterTemplatesForDocument } from "@components/accounting/schedules/documentScheduleTemplates";
import { resolvePickerTemplates } from "@components/accounting/schedules/schedulePickerTemplates";
import { useApplyInvoiceSchedule } from "@components/invoices/useApplyInvoiceSchedule";
import { useInvoiceScheduleCellChange } from "@components/invoices/useInvoiceScheduleCellChange";
import { useOpenInvoiceSchedule } from "@components/invoices/useOpenInvoiceSchedule";
import { useScheduleLifecycleRequests } from "@components/accounting/schedules/useScheduleLifecycleRequests";
import {
    isScheduleCellInteractive,
    toScheduleCardValues,
    resolveInvoiceLineScheduleCellState,
} from "@components/invoices/views/scheduleCellState";
import { ScheduleCellContent } from "@components/invoices/views/ScheduleCellContent";
import {
    InvoiceLineItemsViewCallbacks,
    InvoiceLineItemsViewOptions,
} from "../types";

export const ScheduleCell = memo(
    ({
        row,
        table,
    }: CellContext<ViewTableFeatures, InvoiceLineItem, unknown>) => {
        const {
            applySchedule: runApply,
            isApplying,
            linkedConflictModal,
        } = useApplyInvoiceSchedule();
        const meta = useViewTableMeta<
            InvoiceLineItemsViewCallbacks,
            InvoiceLineItemsViewOptions
        >(table);
        const lineItem = row.original;
        const invoice = lineItem.invoice;
        const state = resolveInvoiceLineScheduleCellState(lineItem);
        const isInteractive = isScheduleCellInteractive(state);
        const entityId = invoice?.entityId;
        const { schedulesExpenses: isExpensesEnabled } = useTypedFlags();

        const scheduleTemplates = useMemo(
            () =>
                filterTemplatesForDocument({
                    templates: resolvePickerTemplates({
                        templates: meta.options?.scheduleTemplates,
                        entityId,
                        prioritizedTemplateId:
                            meta.options?.prioritizedScheduleTemplateId,
                    }),
                    documentType: ScheduleSourceType.invoice,
                    isExpensesEnabled,
                }),
            [
                isExpensesEnabled,
                meta.options?.scheduleTemplates,
                meta.options?.prioritizedScheduleTemplateId,
                entityId,
            ],
        );

        const applySchedule = useCallback(
            async (template: SchedulePickerTemplateWithAnchors) => {
                if (!invoice || entityId == null) {
                    return false;
                }
                const amount = getInvoiceScheduleAmount({
                    lines: invoice.lines,
                    sourceLineIds: [lineItem.id],
                    fallbackAmount: lineItem.amount,
                });
                if (opensScheduleModalBeforeApply(template)) {
                    return (
                        (await meta.callbacks?.onRequestScheduleConfirm?.({
                            invoice,
                            template,
                            sourceLineIds: [lineItem.id],
                            amount,
                            sourceLabel:
                                lineItem.description ??
                                invoice.invoiceNumber ??
                                "Invoice line",
                        })) ?? false
                    );
                }
                return await runApply({
                    templateId: template.id,
                    target: {
                        entityId,
                        invoiceId: invoice.id,
                        amount,
                        sourceLineIds: [lineItem.id],
                    },
                    onApplied: meta.callbacks?.onScheduleApplied,
                });
            },
            [entityId, invoice, lineItem, meta.callbacks, runApply],
        );

        const { onSelect, onClear, confirmModal, isRemoving } =
            useInvoiceScheduleCellChange({
                entityId,
                state: isInteractive ? state : null,
                applySchedule,
                onScheduleChanged: meta.callbacks?.onScheduleApplied,
            });

        const appliedState = state.kind === "applied" ? state : null;
        const { onChangeSchedule, onResumeSchedule } =
            useScheduleLifecycleRequests({
                applicationId: appliedState?.scheduleApplicationId ?? null,
                status: appliedState?.status ?? null,
                entityId,
                sourceType: ScheduleSourceType.invoice,
                requestLifecycleAction:
                    meta.callbacks?.onRequestLifecycleAction,
            });
        const onViewSchedule = useOpenInvoiceSchedule(state);

        if (!isInteractive) {
            return (
                <ViewTableCell>
                    <ScheduleCellContent state={state} />
                </ViewTableCell>
            );
        }

        return (
            <ViewTableCell captureClicks>
                <SchedulePicker
                    templates={scheduleTemplates}
                    applied={toScheduleCardValues(state)}
                    isDisabled={isApplying || isRemoving}
                    prioritizedTemplateId={
                        meta.options?.prioritizedScheduleTemplateId
                    }
                    getBlockReason={resolveInvoiceTemplateBlockReason}
                    onSelect={onSelect}
                    onClear={onClear}
                    onChangeSchedule={onChangeSchedule}
                    onResumeSchedule={onResumeSchedule}
                    onViewSchedule={onViewSchedule}
                    onCreate={() => {
                        if (entityId != null) {
                            meta.callbacks?.onCreateSchedule?.({
                                entityId,
                            });
                        }
                    }}
                />
                {confirmModal}
                {linkedConflictModal}
            </ViewTableCell>
        );
    },
);

ScheduleCell.displayName = "ScheduleCell";
